import {
  BatchModeNoTransaction,
  BatchOperation,
  BatchOperationAdd,
} from '@rhybus/api'
import { v4 as uuidv4 } from 'uuid'

import { mpfRListsHttp } from '../../assets/ts/api/mpf-r-lists-http'
import { mpfWhoHttp } from '../../assets/ts/api/mpf-who-http'
import {
  COLUMN_NAMES as COL,
  LIST_NAME as INQUIRY_LIST,
} from '../../assets/ts/data/inquiry/inquiry'
import { MetadataContextInterface } from '../../store/metadata-context-provider'
import {
  generateRandomTrueOrFalse,
  getRandomNumberBetween,
  pickRandomArrayItem,
  randomDigitString,
  randomPASCODE,
  randomPhoneNumber,
} from './testing'
import TEST_DATA from './testing-testdata'

let inquiryIdCounter = 1

function createRandomInquiryItem(me, metadata: MetadataContextInterface) {
  const item = {}
  const now = new Date()

  item[COL.InquiryID] = createRandomInquiryID()
  //item[COL.InquiryID] = createSequentialInquiryID(1)
  item[COL.InquiryGUID] = uuidv4()
  item[COL.Subject] = pickRandomArrayItem(TEST_DATA.Subject)
  item[COL.Description] = pickRandomArrayItem(TEST_DATA.Description)
  item[COL.AssignedTo] = null

  // To-do: link the section, category, subcategory so we're picking a valid
  // set of values.  (A category that belongs to the given section, a
  // subcategory that belongs to that category)
  const section = pickRandomArrayItem(metadata.sections)
  const category = pickRandomArrayItem(metadata.categories)
  const majcom = pickRandomArrayItem(metadata.majcoms)
  const component = pickRandomArrayItem(metadata.components)
  const status = pickRandomArrayItem(metadata.inquiryStatuses)

  item[COL.StatusCode] = status.StatusCode
  item[COL.SectionCode] = section.SectionCode
  item[COL.CategoryCode] = category.CategoryCode
  item[COL.ServicingBy] = item[COL.CurrentlyServicingBy] = pickRandomArrayItem([
    'MPF',
    'CSS',
  ])
  item[COL.IsArchived] = generateRandomTrueOrFalse()
  item[COL.Customer] = me.rid
  item[COL.DoDNumber] = randomDigitString(10)
  item[COL.DutyEmail] = pickRandomArrayItem(TEST_DATA.EmailAddresses)
  item[COL.DutyPhone] = randomPhoneNumber()
  item[COL.CustomerStatus] = pickRandomArrayItem([
    'Active Duty',
    'Civilian',
    'Contractor',
  ])
  item[COL.Rank] = pickRandomArrayItem(TEST_DATA.Ranks)
  item[COL.OrgMAJCOM] = majcom.MAJCOM
  item[COL.Organization] = pickRandomArrayItem(TEST_DATA.Organizations)
  item[COL.Installation] = pickRandomArrayItem(TEST_DATA.Installations)
  item[COL.PASCODE] = randomPASCODE()
  item[COL.PascodeID] = getRandomNumberBetween(1, 20)
  item[COL.PASCODEServicedBy] = pickRandomArrayItem(['MPF', 'CSS'])
  item[COL.InquiryType] = pickRandomArrayItem(['Self', 'Other'])
  // todo - use org metadata
  item[COL.ServicingOrganization] = pickRandomArrayItem(['96 MPF'])
  item[COL.ServicingMAJCOMCode] = getRandomNumberBetween(146, 158)
  item[COL.AssignedComponentCode] = component.ComponentCode

  item[COL.TeamMembers] = []
  item[COL.InvolvedUsers] = []

  item[COL.ItemCreated] = generateRandomDateBetween('2024-04-01', now)
  item[COL.ItemModified] = now
  item[COL.ItemCreatedBy] = item[COL.ItemModifiedBy] = me.rid

  item[COL.FY] = 2024

  return item
}
function generateRandomDateBetween(startDate, endDate) {
  const convertedStartDate = new Date(startDate)
  const convertedEndDate = new Date(endDate)
  const startTimestamp = convertedStartDate.getTime()
  const endTimestamp = convertedEndDate.getTime()
  const randomTimestamp =
    startTimestamp + Math.random() * (endTimestamp - startTimestamp)
  const randomDate = new Date(randomTimestamp)
  return randomDate
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function createSequentialInquiryID(prefix: number) {
  const prefixString = prefix.toString().padStart(10, '0')
  const suffix = inquiryIdCounter.toString().padStart(7, '0')

  inquiryIdCounter++

  return `${prefixString} - ${suffix}`
}

function createRandomInquiryID() {
  const prefix = randomDigitString(10)
  const suffix = randomDigitString(7)

  return `${prefix} - ${suffix}`
}

async function createInquiries(
  totalNumToCreate,
  metadata: MetadataContextInterface
) {
  const start = performance.now()
  const batchSize = 100
  let numCreated = 0

  const who = mpfWhoHttp()
  const me = await who.Me()

  console.log('me: ', me)

  while (numCreated < totalNumToCreate) {
    const numToCreateThisBatch = Math.min(
      batchSize,
      totalNumToCreate - numCreated
    )

    const batch = createOneBatchAddInquiries(me, numToCreateThisBatch, metadata)
    await batch.execute()

    numCreated += numToCreateThisBatch
    console.log(`Created ${numCreated} / ${totalNumToCreate} inquiries`)
  }
  const end = performance.now()

  console.log(
    `Created ${totalNumToCreate} items in ${(end - start) / 1000} sec`
  )
}

function createOneBatchAddInquiries(
  me,
  batchSize,
  metadata: MetadataContextInterface
) {
  const lists = mpfRListsHttp()

  const batchItems: BatchOperation[] = []
  for (let n = 0; n < batchSize; n++) {
    const item = createRandomInquiryItem(me, metadata)

    batchItems.push({
      data: item,
      list: INQUIRY_LIST,
      op: BatchOperationAdd,
    } as BatchOperation)
  }

  return lists.createBatch(BatchOperationAdd, batchItems)
}

export { createInquiries, createRandomInquiryItem }
